import React from 'react';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PublicSiteNavbar from '../../components/PublicSiteNavbar';
import PublicSiteFooter from '../../components/PublicSiteFooter';
import NonIndexableHelmet from '../../components/NonIndexableHelmet';
import { getLegalMeta } from '../../data/nonIndexableMeta';
import { useLocale } from '../../contexts/LocaleContext';
import { getDateLocale } from '../../utils/localePaths';
import { COMPANY, HOSTING } from '../../data/legalCompanyInfo';

const PrivacyPage = () => {
  const { locale } = useLocale();
  const dateLoc = getDateLocale(locale);
  return (
    <div className="min-vh-100 bg-light legal-page">
      <NonIndexableHelmet {...getLegalMeta('/legal/privacy')} />
      <PublicSiteNavbar />

      {/* Content */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-3 p-md-4 p-lg-5">
                <h1 className="fw-bold mb-4">Politique de Confidentialité</h1>
                <p className="text-muted mb-4">
                  Dernière mise à jour : {new Date().toLocaleDateString(dateLoc)}
                </p>

                <Alert variant="info" className="border-0">
                  <i className="bi bi-shield-check me-2"></i>
                  <strong>Engagement RGPD</strong>
                  <p className="mb-0 mt-2 small">
                    LEAGORA s'engage à protéger la vie privée de ses utilisateurs et à assurer la
                    confidentialité des données personnelles collectées dans le cadre de l'utilisation
                    du service Assistance Video, en conformité avec le Règlement Général sur la Protection
                    des Données (RGPD).
                  </p>
                </Alert>

                <hr className="my-4" />

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">1. Responsable du traitement</h2>
                  <p>
                    Le responsable du traitement des données personnelles est :
                  </p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>Société :</strong> {COMPANY.legalName}</li>
                    <li><strong>Adresse :</strong> {COMPANY.address}</li>
                    <li><strong>SIREN :</strong> {COMPANY.siren}</li>
                    <li><strong>SIRET :</strong> {COMPANY.siret}</li>
                    <li><strong>RCS :</strong> {COMPANY.rcs}</li>
                    <li><strong>N° TVA intracommunautaire :</strong> {COMPANY.vat}</li>
                    <li><strong>Email :</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">2. Données personnelles collectées</h2>
                  <p>
                    Dans le cadre de l'utilisation du service Assistance Video, nous collectons les
                    données personnelles suivantes :
                  </p>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">2.1. Données d'identification</h3>
                  <ul>
                    <li>Adresse email (obligatoire pour la création de compte)</li>
                    <li>Nom et prénom (optionnel)</li>
                    <li>Nom de l'entreprise (optionnel)</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">2.2. Données de connexion</h3>
                  <ul>
                    <li>Adresse IP</li>
                    <li>Date et heure de connexion</li>
                    <li>Type de navigateur et système d'exploitation</li>
                    <li>Tokens de connexion (magic links)</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">2.3. Données d'utilisation du service</h3>
                  <ul>
                    <li>Historique des sessions vidéo (date, durée, participants)</li>
                    <li>Liens de dépannage créés</li>
                    <li>Crédits disponibles et consommés</li>
                    <li>Statistiques d'utilisation</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">2.4. Données de paiement</h3>
                  <ul>
                    <li>Historique des transactions</li>
                    <li>Montants payés</li>
                    <li>Données de facturation</li>
                  </ul>
                  <p className="small text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Note : Les données bancaires (numéro de carte, etc.) sont collectées et traitées
                    directement par notre prestataire de paiement sécurisé Stripe. LEAGORA n'a jamais
                    accès à ces données bancaires.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">2.5. Données vidéo</h3>
                  <ul>
                    <li>Flux vidéo et audio en temps réel (non enregistrés par défaut)</li>
                    <li>Métadonnées des sessions (qualité, bande passante, etc.)</li>
                  </ul>
                  <Alert variant="success" className="mt-3 border-0">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    <strong>Protection maximale :</strong> Les flux vidéo sont chiffrés de bout en bout
                    et ne sont pas enregistrés sauf activation explicite de l'option d'enregistrement
                    par l'utilisateur.
                  </Alert>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">3. Finalités du traitement</h2>
                  <p>Les données personnelles collectées sont utilisées pour les finalités suivantes :</p>
                  <ul>
                    <li><strong>Fourniture du service :</strong> Création de compte, authentification, gestion des sessions vidéo</li>
                    <li><strong>Gestion commerciale :</strong> Traitement des commandes, facturation, gestion des crédits</li>
                    <li><strong>Support client :</strong> Réponse aux demandes d'assistance et de support technique</li>
                    <li><strong>Amélioration du service :</strong> Analyse des statistiques d'utilisation, optimisation des performances</li>
                    <li><strong>Sécurité :</strong> Prévention de la fraude, détection des abus, sécurisation des accès</li>
                    <li><strong>Obligations légales :</strong> Respect des obligations comptables, fiscales et réglementaires</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">4. Base légale du traitement</h2>
                  <p>Le traitement de vos données personnelles repose sur les bases légales suivantes :</p>
                  <ul>
                    <li><strong>Exécution du contrat :</strong> Fourniture du service Assistance Video</li>
                    <li><strong>Consentement :</strong> Acceptation des CGU et CGV lors de la création du compte</li>
                    <li><strong>Intérêt légitime :</strong> Amélioration du service, sécurité, prévention de la fraude</li>
                    <li><strong>Obligation légale :</strong> Conservation des données de facturation, obligations fiscales</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">5. Destinataires des données</h2>
                  <p>Vos données personnelles peuvent être partagées avec :</p>
                  <ul>
                    <li><strong>Personnel autorisé de LEAGORA :</strong> Pour la gestion du service et le support client</li>
                    <li><strong>Prestataires techniques :</strong>
                      <ul className="mt-2">
                        <li>Stripe (traitement des paiements)</li>
                        <li>{HOSTING.name} (hébergement applicatif, France)</li>
                        <li>Galene (plateforme de visioconférence)</li>
                        <li>Services d'envoi d'emails (Mailjet ou SMTP)</li>
                      </ul>
                    </li>
                    <li><strong>Autorités légales :</strong> Sur demande légale ou judiciaire</li>
                  </ul>
                  <p className="small text-muted mt-3">
                    <i className="bi bi-info-circle me-1"></i>
                    Tous nos prestataires sont soumis à des obligations de confidentialité et ne peuvent
                    utiliser vos données qu'aux fins pour lesquelles nous les leur communiquons.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">6. Transfert de données hors UE</h2>
                  <p>
                    L'hébergement principal du service est assuré en France par {HOSTING.name}.
                    Certains prestataires (notamment Stripe pour le paiement) peuvent toutefois traiter
                    certaines données en dehors de l'Union Européenne. Dans ce cas, nous nous assurons
                    que des garanties appropriées sont mises en place (clauses contractuelles types de
                    la Commission Européenne, certifications ISO 27001 ou PCI-DSS, etc.) pour assurer un
                    niveau de protection adéquat de vos données.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">7. Durée de conservation</h2>
                  <p>Vos données personnelles sont conservées pour les durées suivantes :</p>
                  <ul>
                    <li><strong>Données de compte :</strong> Pendant toute la durée d'utilisation du service + 3 ans après la dernière activité</li>
                    <li><strong>Comptes jamais activés :</strong> Supprimés automatiquement 7 jours après l'inscription si l'email n'a pas été vérifié</li>
                    <li><strong>Données de facturation :</strong> 10 ans (obligation légale comptable)</li>
                    <li><strong>Historique des sessions :</strong> 3 ans après la session</li>
                    <li><strong>Logs de connexion :</strong> 1 an maximum</li>
                    <li><strong>Flux vidéo :</strong> Non conservés (sauf enregistrement explicite)</li>
                  </ul>
                  <p className="small text-muted mt-3">
                    À l'issue de ces durées, vos données sont supprimées ou anonymisées de manière irréversible.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">8. Sécurité des données</h2>
                  <p>
                    LEAGORA met en œuvre toutes les mesures techniques et organisationnelles appropriées
                    pour protéger vos données personnelles contre la perte, l'utilisation abusive, l'accès
                    non autorisé, la divulgation, l'altération ou la destruction :
                  </p>
                  <ul>
                    <li>Chiffrement des communications (HTTPS/TLS)</li>
                    <li>Chiffrement de bout en bout des flux vidéo</li>
                    <li>Authentification sécurisée (magic links avec expiration)</li>
                    <li>Hébergement sécurisé avec sauvegardes régulières</li>
                    <li>Accès restreint aux données (principe du moindre privilège)</li>
                    <li>Surveillance et détection des incidents de sécurité</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">9. Vos droits</h2>
                  <p>
                    Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
                  </p>
                  <ul>
                    <li><strong>Droit d'accès :</strong> Obtenir une copie de vos données personnelles</li>
                    <li><strong>Droit de rectification :</strong> Corriger vos données inexactes ou incomplètes</li>
                    <li><strong>Droit à l'effacement :</strong> Demander la suppression de vos données</li>
                    <li><strong>Droit à la limitation :</strong> Limiter le traitement de vos données</li>
                    <li><strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré</li>
                    <li><strong>Droit d'opposition :</strong> Vous opposer au traitement de vos données</li>
                    <li><strong>Droit de retirer votre consentement :</strong> À tout moment</li>
                  </ul>
                  <p className="mt-3">
                    Pour exercer ces droits, vous pouvez nous contacter à l'adresse :{' '}
                    <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>
                  </p>
                  <p className="small text-muted">
                    Nous nous engageons à répondre à votre demande dans un délai d'un mois maximum.
                    Une pièce d'identité pourra vous être demandée pour vérifier votre identité.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">10. Cookies</h2>
                  <p>
                    Le site Assistance Video utilise uniquement des cookies techniques strictement nécessaires
                    au fonctionnement du service :
                  </p>
                  <ul>
                    <li>Cookies de session (authentification)</li>
                    <li>Cookies de préférences (langue, paramètres d'affichage)</li>
                  </ul>
                  <p className="mt-3">
                    Nous n'utilisons pas de cookies publicitaires ou de tracking tiers. Les cookies techniques
                    ne nécessitent pas de consentement préalable conformément à la réglementation en vigueur.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">11. Réclamation</h2>
                  <p>
                    Si vous estimez que vos droits ne sont pas respectés, vous avez le droit d'introduire
                    une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
                  </p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>CNIL</strong></li>
                    <li>3 Place de Fontenoy - TSA 80715</li>
                    <li>75334 PARIS CEDEX 07</li>
                    <li>Tél : 01 53 73 22 22</li>
                    <li>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer">www.cnil.fr</a></li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">12. Modifications</h2>
                  <p>
                    LEAGORA se réserve le droit de modifier la présente politique de confidentialité à tout moment.
                    En cas de modification substantielle, nous vous en informerons par email ou via une notification
                    sur le site. La date de dernière mise à jour est indiquée en haut de cette page.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">13. Contact</h2>
                  <p>
                    Pour toute question concernant cette politique de confidentialité ou le traitement de vos
                    données personnelles, vous pouvez nous contacter :
                  </p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>Par email :</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                    <li><strong>Par courrier :</strong> {COMPANY.legalName} - {COMPANY.address}</li>
                  </ul>
                </section>
              </Card.Body>
            </Card>

            {/* Navigation vers autres pages légales */}
            <div className="mt-4 text-center">
              <p className="text-muted small mb-2">Voir aussi :</p>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Link to="/legal/mentions/" className="text-decoration-none">Mentions légales</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/cgu/" className="text-decoration-none">CGU</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/cgv/" className="text-decoration-none">CGV</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/rgpd/" className="text-decoration-none">RGPD</Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <PublicSiteFooter />
    </div>
  );
};

export default PrivacyPage;

